import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";
import { Link } from "react-router-dom";
import { Globe, Accessibility, Users, Heart, Scale, ArrowRight } from "lucide-react";

export default function Diversity() {
  usePageTitle("Diversity & Inclusion — Central Rentals Canada");
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-br from-violet-50 via-white to-emerald-50">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="diversity-hero">
          <div className="text-xs tracking-[0.22em] uppercase font-bold text-violet-700">Diversity &amp; Inclusion</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 mt-3">
            Housing for everyone. Software for everyone who provides it.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl leading-relaxed">
            Canadian rentals serve newcomers, students, multi-generational families,
            and small landlords from every background. Our platform has to work
            for all of them — and so does the team building it.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Our pillars</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
            Four commitments we hold ourselves to.
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {[
              {
                icon: Users,
                t: "Inclusive hiring",
                d: "Blind resume reviews, structured interview rubrics, and salary bands posted on every job description. We publish our hiring demographics annually so our customers and team can hold us accountable.",
              },
              {
                icon: Accessibility,
                t: "Accessible by default",
                d: "WCAG 2.2 AA targeting across every screen, keyboard navigation, screen-reader-tested forms, and high-contrast modes. CRC Inc. guarantees equal access in full compliance with the Accessible Canada Act.",
              },
              {
                icon: Scale,
                t: "Fair housing assistance",
                d: "Built-in screening guards: applicants can't be filtered or scored by protected characteristics under any provincial Human Rights Code. We surface compliant rejection reasons, not silent biases.",
              },
              {
                icon: Heart,
                t: "Indigenous reconciliation",
                d: "We acknowledge that our team works across the traditional territories of First Nations, Inuit, and Métis peoples. We commit 1% of revenue to Canadian Indigenous-led housing initiatives and partner with First Nations property managers.",
              },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="rounded-2xl border border-slate-200 p-6 hover:border-violet-300 hover:shadow-md transition-all" data-testid={`diversity-pillar-${t.toLowerCase().split(" ")[0]}`}>
                <div className="w-11 h-11 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5"/>
                </div>
                <div className="font-display text-lg font-bold text-slate-900">{t}</div>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="bg-slate-50 border-y border-slate-200 py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 text-center">Our commitments</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 text-center mt-2">
            What we hold ourselves to.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-10 text-center">
            {[
              ["Posted", "Salary band on every job description"],
              ["WCAG 2.2 AA", "Accessibility target on every screen"],
              ["1%", "Of revenue to Indigenous-led housing initiatives"],
            ].map(([n, l]) => (
              <div key={l} className="rounded-2xl bg-white border border-slate-200 p-5">
                <div className="font-display text-2xl md:text-3xl font-extrabold text-violet-700">{n}</div>
                <div className="text-xs text-slate-500 mt-1 leading-snug">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Land acknowledgement */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-5 md:px-8 rounded-3xl bg-gradient-to-br from-amber-50 via-white to-emerald-50 border border-amber-200 p-8 md:p-10">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-700">Land acknowledgement</div>
          <p className="mt-3 text-slate-700 leading-relaxed">
            We acknowledge that our team works on the traditional territories of First Nations,
            Inuit, and Métis peoples across what is now called Canada. We honour the past,
            present, and future relationships with the land and its peoples, and we commit
            to building a platform that supports housing equity for Indigenous landlords,
            tenants, and communities.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 border-t border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">
            Want to build this with us?
          </h2>
          <p className="mt-3 text-slate-600">We're hiring across engineering, customer success, and marketing.</p>
          <Link to="/careers" data-testid="diversity-cta-careers"
            className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-violet-600 text-white font-bold text-base shadow-lg hover:bg-violet-700 hover:shadow-xl transition-all">
            See open roles <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </section>
    </div>
  );
}
